const UUID_SEGMENT_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const ROOT_LOADER_ADDRESS_PREVIEW_EVENT = 'cake:root-loader-address-preview';

export type RootLoaderAddressParts = {
  street: string | null;
  unitNumber?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

export type RootLoaderAddressPreviewDetail = {
  address: string;
  addressUUID?: string;
  lat?: number;
  lng?: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function sanitizeRootLoaderAddressPreview(value: unknown): RootLoaderAddressPreviewDetail | null {
  if (!isRecord(value) || typeof value.address !== 'string' || value.address.trim() === '') {
    return null;
  }

  const address = value.address.trim();
  return {
    address,
    ...(typeof value.addressUUID === 'string' && UUID_SEGMENT_RE.test(value.addressUUID)
      ? { addressUUID: value.addressUUID }
      : {}),
    ...(typeof value.lat === 'number' && Number.isFinite(value.lat) ? { lat: value.lat } : {}),
    ...(typeof value.lng === 'number' && Number.isFinite(value.lng) ? { lng: value.lng } : {}),
  };
}

export function publishRootLoaderAddressPreview(
  detail: RootLoaderAddressPreviewDetail | null,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(ROOT_LOADER_ADDRESS_PREVIEW_EVENT, {
      detail: detail ? sanitizeRootLoaderAddressPreview(detail) : null,
    }),
  );
}

export function extractRootLoaderAddressUuid(pathname: string): string | null {
  let addressUuid: string | null = null;
  for (const segment of pathname.split('/')) {
    if (UUID_SEGMENT_RE.test(segment)) {
      addressUuid = segment;
    }
  }
  return addressUuid;
}

export function getRootLoaderAddressPreview(
  address: RootLoaderAddressParts | null | undefined,
): RootLoaderAddressPreviewDetail | null {
  if (!address?.street) {
    return null;
  }

  const unit = address.unitNumber ? ` #${address.unitNumber}` : '';
  const stateAndPostalCode = [address.state, address.postalCode].filter(Boolean).join(' ');
  const locality = [address.city, stateAndPostalCode].filter(Boolean).join(', ');
  const displayAddress = locality
    ? `${address.street}${unit}, ${locality}`
    : `${address.street}${unit}`;

  return {
    address: displayAddress,
    ...(typeof address.latitude === 'number' && Number.isFinite(address.latitude)
      ? { lat: address.latitude }
      : {}),
    ...(typeof address.longitude === 'number' && Number.isFinite(address.longitude)
      ? { lng: address.longitude }
      : {}),
  };
}

export function publishRootLoaderAddressPartsPreview(
  addressUUID: string | null | undefined,
  address: RootLoaderAddressParts | null | undefined,
): void {
  if (!addressUUID) {
    return;
  }

  const preview = getRootLoaderAddressPreview(address);
  if (!preview) {
    return;
  }

  publishRootLoaderAddressPreview({ ...preview, addressUUID });
}
